import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { css } from "../../../../styled-system/css";
import { HStack, VStack } from "../../../../styled-system/jsx";
import { Button } from "../../../components/core/basic/Button";
import type { BulkAdUpdateError, BulkAdUpdateResult } from "../../../zodios/apiDefinitions/api/ads";

export type BulkAdUpdateGroupConfig = Record<
  string,
  {
    title: string;
    getDetail?: (error: BulkAdUpdateError) => string | undefined;
  }
>;

interface Props {
  results: BulkAdUpdateResult[];
  groupConfig: BulkAdUpdateGroupConfig;
  onDone: () => void;
}

type SkipGroup = {
  key: string;
  title: string;
  rows: Array<{ ad_id: number; ad_name: string; detail?: string }>;
};

export const BulkAdUpdateFeedbackView = ({ results, groupConfig, onDone }: Props) => {
  const { t } = useTranslation(["advertising"]);
  const { successCount, errorCount, groups } = useMemo(() => {
    const success = results.filter((r) => !r.error).length;
    const otherTitle = t(($) => $.advertising.Other);

    const groupMap = new Map<string, SkipGroup>();
    const ensureGroup = (key: string, title: string): SkipGroup => {
      let group = groupMap.get(key);
      if (!group) {
        group = { key, title, rows: [] };
        groupMap.set(key, group);
      }
      return group;
    };

    for (const result of results) {
      if (!result.error) continue;
      const config = groupConfig[result.error.code];
      if (config) {
        ensureGroup(result.error.code, config.title).rows.push({
          ad_id: result.ad_id,
          ad_name: result.ad_name,
          detail: config.getDetail?.(result.error),
        });
      } else {
        ensureGroup("OTHER", otherTitle).rows.push({
          ad_id: result.ad_id,
          ad_name: result.ad_name,
          detail: result.error.message,
        });
      }
    }
    return {
      successCount: success,
      errorCount: results.length - success,
      groups: Array.from(groupMap.values()),
    };
  }, [results, groupConfig, t]);

  return (
    <>
      <p className={css({ color: "gray.700", fontSize: "medium", textAlign: "center" })}>
        {t(($) => $.advertising["{count} media plan updates were not made for the reasons noted below."], {
          count: errorCount,
        })}
        <span>
          {successCount > 0 ?
            ` ${t(($) => $.advertising["{count} were successfully updated."], { count: successCount })}`
          : ""}
        </span>
      </p>
      <VStack css={{ gap: "large", alignItems: "stretch", paddingY: "medium" }}>
        {groups.map((group) => (
          <section
            key={group.key}
            className={css({
              border: "1px solid",
              borderColor: "gray.200",
              borderRadius: "medium",
              padding: "medium",
            })}
          >
            <h3 className={css({ fontWeight: "semibold", fontSize: "xlarge" })}>
              {t(($) => $.advertising["{title} ({count} media plans)"], {
                title: group.title,
                count: group.rows.length,
              })}
            </h3>
            <VStack
              className="custom-scroll-container"
              css={{ alignItems: "stretch", gap: "xsmall", maxHeight: "240px", overflow: "auto" }}
            >
              <ol>
                {group.rows.map(({ ad_id, ad_name, detail }) => (
                  <li key={ad_id} className={css({ paddingY: "xsmall" })}>
                    <span>
                      {ad_name} <small>{t(($) => $.advertising["(ID: {id})"], { id: ad_id })}</small>
                    </span>
                    {detail ?
                      <span className={css({ color: "gray.600" })}>{detail}</span>
                    : null}
                  </li>
                ))}
              </ol>
            </VStack>
          </section>
        ))}
      </VStack>
      <HStack className={css({ justifyContent: "center" })}>
        <Button onPress={onDone}>{t(($) => $.advertising.Done)}</Button>
      </HStack>
    </>
  );
};
